import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import { memoPublic } from "@/lib/serverMemo";
import { testimonialModerationSchema, testimonialSubmissionSchema } from "./testimonialSchemas";

export type PublicTestimonial = Database["public"]["Tables"]["public_testimonials"]["Row"];
export type PrivateTestimonial = Database["public"]["Tables"]["testimonials"]["Row"];

const publicClient = () => {
  const key = process.env['SUPABASE_PUBLISHABLE_KEY']!;
  return createClient<Database>(process.env['SUPABASE_URL']!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: { fetch: (input, init) => {
      const headers = new Headers(init?.headers);
      if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) headers.delete("Authorization");
      headers.set("apikey", key);
      return fetch(input, { ...init, headers });
    } },
  });
};

const PUBLIC_FIELDS = "testimonial_id,quote,rating,course_id,display_first_name,display_city,child_age,source_type,source_url,reviewed_at,published_at,created_at";

async function loadPublicTestimonials(): Promise<PublicTestimonial[]> {
  const { data, error } = await publicClient()
    .from("public_testimonials")
    .select(PUBLIC_FIELDS)
    .order("published_at", { ascending: false });
  if (error) throw new Error("Published parent stories are temporarily unavailable.");
  return data as PublicTestimonial[];
}

/**
 * Full list for /reviews. Cached per isolate for 60s and bounded at 2.5s so a
 * cold database cannot hold the HTML; `null` means "could not load right now"
 * (never presented as "zero stories").
 */
export const getPublicTestimonials = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicTestimonial[] | null> =>
    (await memoPublic("public_testimonials", 60_000, 2_500, loadPublicTestimonials)) ?? null,
);

/**
 * Lightweight published-story count for the /impact card. Head-only count,
 * cached 5 min, bounded at 1.5s; `null` when unknown so the page uses neutral text.
 */
export const getPublicTestimonialCount = createServerFn({ method: "GET" }).handler(
  async (): Promise<number | null> => {
    const count = await memoPublic("public_testimonials_count", 300_000, 1_500, async () => {
      const { count, error } = await publicClient()
        .from("public_testimonials")
        .select("testimonial_id", { count: "exact", head: true });
      if (error || count === null) throw new Error("count unavailable");
      return count;
    });
    return count ?? null;
  },
);

export const submitTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => testimonialSubmissionSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("testimonials").insert({
      user_id: context.userId,
      quote: data.quote,
      rating: data.rating ?? null,
      course_id: data.courseId ?? null,
      display_first_name: data.displayFirstName ?? null,
      display_city: data.displayCity ?? null,
      child_age: data.childAge ?? null,
      consent_quote: data.consentQuote,
      consent_first_name: data.consentFirstName,
      consent_city: data.consentCity,
      consent_child_age: data.consentChildAge,
      consent_course: data.consentCourse,
    });
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const getMyTestimonials = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.from("testimonials")
      .select("id,quote,rating,moderation_status,is_published,created_at,updated_at")
      .eq("user_id", context.userId).order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data;
  });

export const withdrawMyTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => {
    const parsed = typeof input === "object" && input !== null ? input as { id?: unknown } : {};
    if (typeof parsed.id !== "string" || !/^[0-9a-f-]{36}$/i.test(parsed.id)) throw new Error("Invalid story.");
    return { id: parsed.id };
  })
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("testimonials")
      .update({ moderation_status: "withdrawn", is_published: false, consent_quote: false })
      .eq("id", data.id).eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

async function requireAdmin(context: { supabase: any; userId: string }) {
  const { data, error } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
  if (error || !data) throw new Error("Forbidden: admin only");
}

export const listTestimonialsForAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await requireAdmin(context);
    const { data, error } = await context.supabase.from("testimonials").select("*").order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data;
  });

export const moderateTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => testimonialModerationSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdmin(context);
    const now = new Date().toISOString();
    const patch: Database["public"]["Tables"]["testimonials"]["Update"] = {
      moderation_notes: data.moderationNotes || null,
    };
    if (data.sourceVerified !== undefined) patch.source_verified = data.sourceVerified;
    if (data.sourceType) patch.source_type = data.sourceType;
    if (data.sourceUrl !== undefined) patch.source_url = data.sourceUrl || null;
    if (data.action === "approve") Object.assign(patch, { moderation_status: "approved", reviewed_by: context.userId, reviewed_at: now, is_published: false });
    if (data.action === "reject") Object.assign(patch, { moderation_status: "rejected", reviewed_by: context.userId, reviewed_at: now, is_published: false });
    if (data.action === "withdraw") Object.assign(patch, { moderation_status: "withdrawn", is_published: false });
    if (data.action === "publish") Object.assign(patch, { moderation_status: "approved", reviewed_by: context.userId, reviewed_at: now, is_published: true });
    if (data.action === "unpublish") patch.is_published = false;
    const { error } = await context.supabase.from("testimonials").update(patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
